import { Check, LoaderCircle, RotateCcw, Sliders } from "lucide-react";
import { useEffect, useState } from "react";
import { apiClient, type CenaValue, type CenaWarning } from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { ApiState } from "@/shared/components/ApiState";
import { ConfirmDialog } from "@/shared/components/ConfirmDialog";
import { useUnsavedChanges } from "@/shared/unsaved-changes/UnsavedChanges";
import { CenaSaveBar, ConfigForm, WarningList, changedFields } from "@/features/admin/CenaAdmin";

type Values = Record<string, CenaValue>;

/**
 * Ajustes → Cadastro → pesos do CENA da empresa ATIVA (2026-10-22).
 *
 * A proposta de IAM dá "Configurar o CENA da empresa" ao Máster, ao
 * Administrador e ao Especialista, cada um no seu escopo. Até então o
 * override de pesos só se editava em Administração → CENA, pelo Máster;
 * aqui ele vale pra empresa aberta (`/cena/score-config`, empresa no
 * `X-Company-Id`) com o MESMO formulário daquela tela.
 *
 * **Override herda tudo ou nada**, como lá: criar a configuração própria
 * copia os pesos padrão, e "Voltar ao padrão" a apaga. Editar peso não
 * recalcula o que já foi calculado.
 *
 * `canEdit` falso (Técnico e Visualizador) mostra os pesos sem gravar.
 */
export function CompanyCenaWeights({ token, canEdit }: { token: string; canEdit: boolean }) {
  const [hasOverride, setHasOverride] = useState(false);
  const [defaults, setDefaults] = useState<Values | null>(null);
  const [saved, setSaved] = useState<Values | null>(null);
  const [values, setValues] = useState<Values | null>(null);
  const [warnings, setWarnings] = useState<CenaWarning[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmingReset, setConfirmingReset] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .companyCena(token)
      .then(config => {
        if (cancelled) return;
        setHasOverride(config.has_override);
        setDefaults(config.default_values);
        setSaved(config.values);
        setValues(config.values);
      })
      .catch(failure => {
        if (!cancelled) setError(errorMessage(failure, "Não foi possível carregar os pesos do CENA."));
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const save = async (): Promise<boolean> => {
    if (!values) return false;
    setBusy(true);
    setError(null);
    try {
      const result = await apiClient.saveCompanyCena(token, values);
      setHasOverride(true);
      setSaved(result.config.values);
      setValues(result.config.values);
      setWarnings(result.warnings);
      return true;
    } catch (failure) {
      setError(errorMessage(failure, "Não foi possível salvar os pesos."));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setBusy(true);
    setError(null);
    try {
      await apiClient.clearCompanyCena(token);
      setHasOverride(false);
      setSaved(defaults);
      setValues(defaults);
      setWarnings([]);
      setConfirmingReset(false);
    } catch (failure) {
      setError(errorMessage(failure, "Não foi possível voltar ao padrão."));
    } finally {
      setBusy(false);
    }
  };

  // Sair com peso alterado e não salvo pergunta antes (`shared/unsaved-changes`).
  useUnsavedChanges(canEdit && values !== null && changedFields(values, saved).length > 0, save, "os pesos do CENA da empresa");

  if (error && !values) return <p className="form-error" role="alert">{error}</p>;
  if (!values) return <ApiState kind="loading">Carregando os pesos do CENA…</ApiState>;

  return (
    <div className="ai-admin-stack">
      <p className="section-hint">
        {hasOverride
          ? "Esta empresa tem pesos próprios: os padrão da instalação não valem para ela."
          : "Esta empresa usa os pesos padrão da instalação. Salvar uma mudança aqui cria a configuração própria dela, a partir de uma cópia dos padrão."}{" "}
        Mudar um peso não recalcula o que já foi calculado: vale para uploads, amostras e correções daqui pra frente.
      </p>
      {error && <p className="form-error" role="alert">{error}</p>}
      <WarningList warnings={warnings} />
      <ConfigForm
        values={values}
        saved={saved}
        disabled={busy || !canEdit}
        onChange={(field, value) => setValues({ ...values, [field]: value })}
      />
      {canEdit && (
        <CenaSaveBar
          pending={changedFields(values, saved).length}
          saving={busy}
          onDiscard={() => saved && setValues(saved)}
        >
          {hasOverride && (
            <button className="secondary-button" type="button" disabled={busy} onClick={() => setConfirmingReset(true)}>
              <RotateCcw size={16} /> Voltar ao padrão
            </button>
          )}
          <button
            className="primary-button"
            type="button"
            disabled={busy || changedFields(values, saved).length === 0}
            onClick={() => void save()}
          >
            {busy ? <LoaderCircle className="spin" size={16} /> : hasOverride ? <Check size={16} /> : <Sliders size={16} />}{" "}
            {hasOverride ? "Salvar pesos" : "Criar pesos próprios"}
          </button>
        </CenaSaveBar>
      )}
      {confirmingReset && (
        <ConfirmDialog
          title="Voltar aos pesos padrão?"
          description="Os pesos próprios desta empresa são apagados e ela passa a usar os padrão da instalação. A mudança fica na auditoria."
          confirmLabel="Voltar ao padrão"
          loading={busy}
          onConfirm={() => void reset()}
          onCancel={() => setConfirmingReset(false)}
        />
      )}
    </div>
  );
}
